import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import PortalDocument from "../../../document/PortalDocument";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {DeleteDocumentType} from "../../../../shared/models/DeleteDocumentType";
import {Information, SignatureEditProfile} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export class DeleteDialogDefinition extends PortalDialog<DeleteDocumentType,
	PortalDialogProperties<DeleteDocumentType>, PortalDialogState<DeleteDocumentType>> {
	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		for (let selectedDocument of selectedDocuments) {
			if (selectedDocument.isLocked()) {
				return false;
			}
		}

		return selectedDocuments.length > 0;
	}

	public renderDialog(): ReactNode {
		let parameter: DeleteDocumentType | undefined = this.state.parameter?.data;
		let filename: string | undefined;

		if (parameter?.documentIds?.length === 1) {
			try {
				let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
					parameter?.documentIds[0]
				);
				filename = portalDocument.getFileName();
			} catch (e: any) {
				// can be ignored
			}
		}

		let classList: Array<string> = ["delete-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.delete.general"} namespace={"help"} markdown={true}/>
					</Helper>
					<fieldset className="p-col-12 p-grid">
						<div className="dialog-message">
							{!FunctionHelper.exists(filename) ?
								<Translate {...this.props} data="delete.multi" namespace="dialog"/> :
								<Translate {...this.props} data="delete.single" namespace="dialog"
										   values={{filename: filename}}/>
							}
						</div>
					</fieldset>
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-delete small"}/>
					<Translate {...this.props} data="delete.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected renderFooter(): ReactNode {
		return (
			<div>
				<Button label={Translate.value("buttons.yes")} className="primary-button"
						icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
						disabled={!this.isValid()} onClick={() => this.submit()}/>
				<Button label={Translate.value("buttons.no")} icon="icon icon-close" onClick={() => this.hide()}
						className="dialog-button"/>
			</div>
		);
	}

	protected isValid(): boolean {
		if (!this.mounted || !this.props.visible) {
			return false;
		}

		return !this.state.submitted;
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let operationData: DeleteDocumentType = this.state.parameter!.data.clone();
		let documentManager: PortalDocumentManager = this.props.context.documentManager;

		try {
			await documentManager.deleteDocuments(FunctionHelper.applyValue(
				operationData.documentIds, DeleteDocumentType.getDocumentIdsDefault()
			));
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		let message: Message = new Message("success.document.delete", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.hide();
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.DELETE;
	}

	protected getDefaultParameter(): undefined {
		return;
	}

	protected isEditPasswordNeeded(): boolean {
		return false; // Don't check for password
	}
}

const DeleteDialog = React.memo(DeleteDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<DeleteDocumentType>>,
		nextProps: Readonly<PortalDialogProperties<DeleteDocumentType>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default DeleteDialog;